import { leaveOrganisation } from '@/api/organisations';
import { removeItem } from '@/core/storage';
import useWindow from '@/hooks/useWindow';
import ApiIcon from '@mui/icons-material/Api';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PasswordIcon from '@mui/icons-material/Password';
import PaymentIcon from '@mui/icons-material/Payment';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { Badge, Link } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import numeral from 'numeral';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { logout, updateUserAccount } from '../api/auth';
import { AccountContext } from '../context/account';
import { OrderRequestItem, OrganisationDetails, UserRoles } from '../core/global';
import { featuresDescription, permissionsModal, tokensModal } from '../data/content';
import { userProfileShape } from '../data/form-shapes';
import { displayUserRole, getAccountLevelLabel, getPurchaseRequestsByType } from '../helpers/core';
import { trackEvent } from '../helpers/tracking';
import styles from "../styles/components/user-settings-modal.module.scss";
import { AddressBookModal } from './address-book-modal';
import { DeleteAccountModal } from './delete-account-modal';
import { ErrorMessage, InfoMessage, SuccessMessage } from './error-message';
import { CreateForm } from './form-components';
import { InfoModal } from './info-modal';
import { ModalTitle } from './modal-title';
import { OrganisationDiagramModal } from './organisation-diagram-modal';
import { OrganisationSettingsModal } from './organisation-settings-modal';
import { PurchaseTokensModal } from './purchase-tokens-modal';
import { ResetPassword } from './reset-password';
import { TokensModal } from './tokens-modal';

export const showApiButton = import.meta.env.VITE_ENABLE_API_ACCESS === 'true'

interface UserSettingsModalProps {
  open: boolean;
  handleClose: () => void;
}

export const UserSettingsModal = ({ open, handleClose }: UserSettingsModalProps) => {
  const [userOrganisationId, setUserOrganisationId] = useState<number | null>(null)
  const [showOrganisationModal, setShowOrganisationModal] = useState(false)
  const [showTokensModal, setShowTokensModal] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [modalError, setModalError] = useState<string[]>([]);
  const [showMessage, setShowMessage] = useState(false)
  const [isEdited, setIsEdited] = useState(false)
  const [enableNotifications, setEnableNotifications] = useState(false)
  const [enable2FA, setEnable2FA] = useState(false)
  const [showEditBilling, setShowEditBilling] = useState(false)
  const [showAccessTokenModal, setShowAccessTokenModal] = useState(false)
  const [showTokenInfoModal, setShowTokenInfoModal] = useState(false)
  const [showUpdatePassword, setUpdatePassword] = useState(false)
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)

  const { 
    account, 
    refetchUserDetails, 
    userTokensTotal,
    getUserTokens,
    companyPurchaseRequests,
    setAccount,
    setShowLoginModal,
  } = useContext(AccountContext);
  
  const _window = useWindow();
  
  const navigate = useNavigate();

  const features = useMemo(() => {
    let features: {
      [key: string]: any
    } = {}
  if (account) {
      account.organisation?.forEach( (org: OrganisationDetails) => {
        const {features: _f} = org
        features = {
          ...features,
          ..._f
        }
      })
    }
    return features
  }, [account])

  useEffect(() =>{
    getUserTokens()
  }, [])

  const onClose = () => {
    setShowMessage(false)
    setModalError([])    
    handleClose()
  }

  useEffect(() => {
    if (open) {
      setShowMessage(false)
      setModalError([])
      setIsEdited(false)
    }
    // refetch each time this opens or closes
    refetchUserDetails()  
  }, [open])

  useEffect(() => {
    if (open && account.uid) {
      trackEvent("user-settings-modal", {uid: account.uid})
    }
  }, [open, account.uid])

  useEffect(() => {
    if (account !== null) {
      setFirstName(account.first_name || '')
      setLastName(account.last_name || '')
      setEnableNotifications(account.notifications || false)
      setEnable2FA(account.enable_2fa || false)
    }
  }, [account, open])

  const validateInputs = () => {
    let isValid = true;
    if (!firstName || !lastName) {
      isValid = false;
      setModalError( e => [...e, `Your first name or last name cannot be empty.`])
    }
    return isValid;
  };

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setModalError([])
    const isValid = validateInputs()
    if (isValid) {
      const execute = async () => {
        const {error, message} = await updateUserAccount({
          first_name: firstName,
          last_name: lastName,
          email: account.email || null,
          // uid: account.uid || null,
          notifications: enableNotifications,
          enable_2fa: enable2FA
        })
        if (error !== 200) {
          setModalError( e => [...e, `${message}`])
        } else {
          setIsEdited(false)
          setShowMessage(true)
          refetchUserDetails()
        }
      }
      execute()      
    }
  };

  const userOrganisationAdmin = useMemo(() => {
    if (account) {
      const {organisation} = account
      if (organisation && Array.isArray(organisation)) {
        return organisation.filter( org => {
          return org.role === 'admin' || org.role === 'super-admin'
        })
      }
    }
    return []
  }, [account])
  
  const userOrganisationNonAdmin = useMemo(() => {
    if (account) {
      const {organisation} = account
      if (organisation && Array.isArray(organisation)) {
        return organisation.filter( org => {
          return org.role === 'member' || org.role === 'external-user'
        })
      }
    }
    return []
  }, [account])

  const hasMultipleOrganisations = useMemo(() => {
    if (account) {
      const {organisation} = account
      return (organisation && Array.isArray(organisation) && organisation.length > 1)
    }
    return false
  }, [account])

  // const activeRequests = useMemo(() => {
  //   return companyPurchaseRequests?.filter(({status}) => status === 'pending' || status === 'processing') 
  // }, [companyPurchaseRequests])

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
      >
        <ModalTitle onClose={onClose}>User settings</ModalTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
        >
          <>
            <div>
              <p>Edit your user account settings.</p>
              <p style={{display: "block"}}>Your account is <strong>{getAccountLevelLabel(account.level)}</strong> account. Your user ID is <strong>#{account.uid}</strong></p>
              {userTokensTotal > 0 && import.meta.env.VITE_ENABLE_TOKENS === "true" &&
                <Badge
                  badgeContent={"?"} 
                  color="info" 
                  onClick={()=> {
                    setShowTokenInfoModal(true)
                  }}
                >
                  <p>You have {numeral(userTokensTotal).format("0,0")} tokens to purchase data.</p>
                </Badge>
              }
              <div style={{display: 'flex', justifyContent: 'space-between'}}>
                {import.meta.env.VITE_ENABLE_TOKENS === "true" && 
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PaymentIcon sx={{color: "#fff"}} />}
                    onClick={() => {
                      setShowTokensModal(true)
                    }}
                  >
                    Purchase {userTokensTotal !== null && userTokensTotal > 0 ? 'more': ''} tokens
                  </Button>
                }
                <Button
                  variant="contained"
                  // color="success"
                  startIcon={<ContactMailIcon sx={{color: "#fff"}} />}
                  onClick={() => {
                    setShowEditBilling(true)
                  }}
                >
                  Address book
                </Button>
                {showApiButton && 
                  <Button
                    variant="contained"
                    // color="success"
                    startIcon={<ApiIcon sx={{color: "#fff"}} />}
                    onClick={() => {
                      setShowAccessTokenModal(true)
                    }}
                  >
                    <Badge
          // className={styles.badge}
          badgeContent={'beta'}
          color="warning" 
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >API access
         </Badge>           
                  </Button>
                }
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PasswordIcon sx={{color: "#fff"}} />}
                  onClick={() => {
                    setUpdatePassword(true)
                  }}
                >
                  Update password  
                </Button>
              </div>
            </div>
            {showMessage && <SuccessMessage message={'Your account has been updated.'} />}
            <ErrorMessage message={modalError} />
            <CreateForm
              size="medium"
              allowEdit={false}
              shape={userProfileShape}
              defaultValues={account}
              onChange={(vals, key) => {
                const {first_name, last_name, notifications, enable_2fa} = vals
                setFirstName(first_name)
                setLastName(last_name)
                setEnableNotifications(notifications)
                setEnable2FA(enable_2fa)
                setIsEdited(true)
              }}
            />
            {
              Object.keys(features).length > 0 && 
              <div className={styles.featureList}>
                <h4>Account features and permissions</h4>
                <InfoMessage message={"This can only be managed by MetOceanWorks team. Please reach out if you have any questions."} />
                {Object.keys(features).map( key => {
                  const f = features[key]
                  let label = key
                  if (featuresDescription && Object.prototype.hasOwnProperty.call(featuresDescription, key)) {
                    // @ts-ignore
                    label = featuresDescription[key].title
                  }
                  return <p key={key}>
                      <strong>{label}</strong>: {typeof f === 'boolean' ? (f ? 'enabled' : 'disabled') : JSON.stringify(f)}
                    </p>
                })}
              </div>
            }
          </>
          <UserSettingsOrganisationList companyPurchaseRequests={companyPurchaseRequests} data={userOrganisationAdmin} onClick={(id) => {
            setUserOrganisationId(Number(id))
          }} />
          <UserSettingsOrganisationList data={userOrganisationNonAdmin} canLeave onLeft={refetchUserDetails} />
          {/* Temporary functionality */}
          {hasMultipleOrganisations && userOrganisationAdmin.length > 2 && 
            <>
              <Link onClick={() => {
                setShowOrganisationModal(true)
              }}>Show your organisation diagram</Link>
            </>
          }

          <Button
            variant="contained"
            // color="success"
            startIcon={<WorkHistoryIcon sx={{color: "#fff"}} />}
            onClick={() => {
              setUserOrganisationId(0)
            }}
          >
            Create a new company
          </Button>

          {(account?.level !== 100 && account?.level !== 80) && 
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setShowDeleteAccount(true)}
              >
                Delete my account
              </Button>
            </div>
          }
        </DialogContent>
        {isEdited ? 
          <>
            <DialogActions sx={{ pb: 3, px: 3 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" type="submit" onClick={onSubmit}>
                Save
              </Button>
            </DialogActions>
          </>
          :
          <>
            <DialogActions sx={{ pb: 3, px: 3 }}>
              <Button onClick={onClose}>Close</Button>
            </DialogActions>
          </>
        }
      </Dialog>
      <OrganisationDiagramModal open={showOrganisationModal} handleClose={() => {
        setShowOrganisationModal(false)
      }} />
      {userOrganisationId !== null &&
        <OrganisationSettingsModal
          id={userOrganisationId}
          handleClose={() => {
            setUserOrganisationId(null)
          }}
          onUpdateOrganisationId={(id: number | string | null) => {
            setUserOrganisationId(id !== null ? Number(id) : null)
          }}
        />
      }
      {showTokensModal && <PurchaseTokensModal 
        onClose={() => {
            setShowTokensModal(false)
          }} 
        />
      }
      <InfoModal
          open={showTokenInfoModal}
          title={tokensModal.title}
          content={tokensModal.content}
          maxWidth="sm"
          onClose={() => {
            setShowTokenInfoModal(false)
          }}
        />
      {showEditBilling &&
        <AddressBookModal 
          onClose={() =>{
            setShowEditBilling(false)
          }}
        />
      }
      {showUpdatePassword && <ResetPassword isModal onClose={()=>{
        setUpdatePassword(false)
      }} />}
      {showAccessTokenModal && <TokensModal
        onClose={() => {
          setShowAccessTokenModal(false)
        }}
      />
      }
      <DeleteAccountModal
        open={showDeleteAccount}
        onClose={() => setShowDeleteAccount(false)}
        onDeleted={() => {
          // Account is gone — end the session and send them out.
          setShowDeleteAccount(false)
          onClose()
          logout();
          setAccount({ loggedIn: false, checked: true });
          removeItem('map-location')
          setShowLoginModal(false);
          navigate('/dashboard');
          _window.setTimeout(() => {
            _window.location.reload();
          }, 100)
        }}
      />
    </>
  );
}

export const UserSettingsOrganisationList = ({
  data, 
  onClick,
  companyPurchaseRequests,
  canLeave = false,
  onLeft,
}: {
  data: any[], 
  companyPurchaseRequests?: OrderRequestItem[] | null,
  onClick?: (id: number | string) => void,
  canLeave?: boolean,
  onLeft?: () => void,
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [leaveTarget, setLeaveTarget] = useState<{ id: number | string; name: string } | null>(null)
  
  const useRoles = useMemo(() => {
    const roles: UserRoles[] = []
    data.forEach( org => {
      if (org.role && !roles.includes(org.role)) {
        roles.push(org.role)
      }
    })
    return [...roles].map(r => displayUserRole(r))
  }, [data])

  if (data.length === 0) {
    return null
  }
  
  return (
    <>
      {
        data.length > 1 ? 
          <p className={styles.par}>Your are {useRoles.join(' and ')} of {numeral(data.length).format("0,0")} organisations listed below.</p>
        :
          <p className={styles.par}>You are {useRoles.join(' and ')} of {data[0].name}.</p>
        }
      {
        data.map((org, index) => {
          const {name, city, country, id, role} = org
          const companyActiveRequests = companyPurchaseRequests ? getPurchaseRequestsByType(companyPurchaseRequests, id, ['pending', 'processing']) : []
          return <div className={styles.row} key={id}>
            <span>
              <strong>{name}</strong><br />{[city, country].filter( i => !!i).join(', ')}
            </span>
            {onClick ?
              <Button
                sx={{
                  maxWidth: "270px",
                }}
                variant="contained"
                startIcon={<ManageAccountsIcon sx={{color: "#fff"}} />}
                onClick={() =>{
                  onClick(id)
                }}
              >
              {companyActiveRequests && companyActiveRequests.length > 0 ?
                <Badge
                  className={styles.badge}
                  badgeContent={numeral(companyActiveRequests.length).format("0,0")} 
                  color="error"
                >
                  Manage
                </Badge>
              :
                <>Manage</>
              }
              </Button>
              :
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Badge
                    className={styles.badge}
                    badgeContent={"?"} 
                    color="info" 
                    onClick={()=> {
                      setShowInfoModal(true)
                    }}
                  >
                    <span><strong>Role:</strong> {displayUserRole(role)}</span>
                </Badge>
                {canLeave &&
                  <Button
                    sx={{
                      width: "100px",
                      minWidth: "100px",
                      maxWidth: "170px",
                    }}
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={() => setLeaveTarget({ id, name })}
                  >
                    Leave
                  </Button>
                }
              </span>
            }
          </div>
        })
      }
  
      <InfoModal
        open={showInfoModal}
        title={permissionsModal.title}
        content={permissionsModal.content}
        maxWidth="sm"
        onClose={() => {
          setShowInfoModal(false)
        }}
      />
      <LeaveOrganisationConfirm
        org={leaveTarget}
        onClose={() => setLeaveTarget(null)}
        onLeft={() => onLeft?.()}
      />
    </>
  )
}

interface LeaveOrganisationConfirmProps {
  org: { id: number | string; name: string } | null;
  onClose: () => void;
  onLeft: () => void; // called after a successful leave so the parent can refetch
}

export const LeaveOrganisationConfirm = ({ org, onClose, onLeft }: LeaveOrganisationConfirmProps) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string[]>([])

  const _onClose = () => {
    setError([])
    onClose()
  }

  const onConfirm = () => {
    if (!org) return
    setError([])
    setLoading(true)
    const execute = async () => {
      const { error: status, message } = await leaveOrganisation({ organisation_id: org.id })
      setLoading(false)
      if (status !== 200) {
        // Backend returns 409 with a helpful message for the last-admin case,
        // 404 if not a member — surface whatever it says rather than guessing.
        setError(e => [...e, `${message}`])
      } else {
        onLeft()
        _onClose()
      }
    }
    execute()
  }

  return (
    <Dialog open={!!org} onClose={loading ? undefined : _onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Leave organisation</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to leave <strong>{org?.name}</strong>? You'll lose access to this
          organisation's shared data and will need to be re-invited to rejoin.
        </DialogContentText>
        <ErrorMessage message={error} />
      </DialogContent>
      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button onClick={_onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading}
          startIcon={<LogoutIcon sx={{ color: '#fff' }} />}
        >
          {loading ? 'Leaving…' : 'Leave organisation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}